/**
 * GST Reports — the master hub for every WhiteBooks-powered GST report:
 *   GSTR-1, GSTR-2B, GSTR-3B, GSTR-6, GSTR-6A, E-Invoice, E-Way Bill,
 *   Cash Ledger, Credit Ledger, Reversal Reclaimable Ledger, RCM Statement,
 *   plus the Filing Status FY dashboard.
 *
 * Auth: uses one-time OTP flow — user submits GSTIN + gst_username → OTP
 * dispatched by GST portal → user enters OTP → session valid 30 days.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, RefreshCw, LogOut, Download,
         AlertCircle, CheckCircle2, FileSpreadsheet } from "lucide-react";
import GstAuthenticateModal from "@/components/gst/GstAuthenticateModal";
import LedgerTrendChart, { normaliseLedgerToSeries, mergeLedgerSeries } from "@/components/LedgerTrendChart";

const REPORTS = [
  { key: "gstr1",           label: "GSTR-1",            kind: "period", sections: ["retsum","b2b","b2cs","b2cl","exp","cdnr","hsnsum","nil","einvoice"], excel: true },
  { key: "gstr2b",          label: "GSTR-2B",           kind: "period", excel: true },
  { key: "gstr3b",          label: "GSTR-3B",           kind: "period", sections: ["retsum","autoliab"], excel: true },
  { key: "gstr6",           label: "GSTR-6 (ISD)",      kind: "period", sections: ["retsum","b2b","cdn","itc"] },
  { key: "gstr6a",          label: "GSTR-6A",           kind: "period", sections: ["b2b","cdn","b2ba","cdna"] },
  { key: "einvoice",        label: "E-Invoice",         kind: "period", sections: ["irnlist","filedtl","hsnsum"], excel: true },
  { key: "ewb",             label: "E-Way Bill",        kind: "period", excel: true },
  { key: "cash-ledger",     label: "Cash Ledger",       kind: "range",  excel: true },
  { key: "credit-ledger",   label: "Credit Ledger",     kind: "range",  excel: true },
  { key: "reversal-ledger", label: "Reversal Reclaim.", kind: "range",  excel: true },
  { key: "rcm-statement",   label: "RCM Statement",     kind: "range",  excel: true },
  { key: "filing-status",   label: "Filing Dashboard",  kind: "fy_only", excel: true },
];

const MONTHS = [
  { v: "04", n: "Apr" }, { v: "05", n: "May" }, { v: "06", n: "Jun" },
  { v: "07", n: "Jul" }, { v: "08", n: "Aug" }, { v: "09", n: "Sep" },
  { v: "10", n: "Oct" }, { v: "11", n: "Nov" }, { v: "12", n: "Dec" },
  { v: "01", n: "Jan" }, { v: "02", n: "Feb" }, { v: "03", n: "Mar" },
];

// Build list of last 6 FYs — includes current and 5 previous
const FY_LIST = (() => {
  const d = new Date();
  const startYr = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = startYr - i;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  });
})();

const nowFy = () => {
  const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
};
const nowMMYYYY = () => { const d = new Date(); return `${String(d.getMonth()+1).padStart(2,"0")}${d.getFullYear()}`; };

export default function GstReports() {
  const [sessions, setSessions] = useState([]);
  const [activeGstin, setActiveGstin] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [tab, setTab] = useState("gstr1");
  const [mode, setMode] = useState("period");   // period | fy
  const [period, setPeriod] = useState(nowMMYYYY());
  const [fy, setFy] = useState(nowFy());
  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt]     = useState("");
  const [section, setSection] = useState("retsum");
  const [loading, setLoading] = useState(false);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [recoBusy, setRecoBusy] = useState(false);
  const [reco,     setReco]     = useState(null);
  const [recoDrill,setRecoDrill]= useState(null);
  const [timeline, setTimeline] = useState(null);
  const [result, setResult]   = useState(null);
  // Iter-133 · Ledger trend chart state — restore missing hooks so oxlint passes
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [ledgerSeries, setLedgerSeries] = useState([]);
  const [bridgeMode, setBridgeMode] = useState(false);

  const loadSessions = async () => {
    try {
      const r = await api.get("/gst-reports/auth/status");
      const list = r.data.sessions || [];
      setBridgeMode(r.data.via === "zenith360_gst");
      setSessions(list);
      // Prefer the first AUTHENTICATED session; only fall back to first-in-list
      // when nothing is active yet (fresh install / all expired).
      const preferred =
        list.find(s => s.authenticated)?.gstin ||
        list[0]?.gstin || "";
      // Re-anchor whenever the current activeGstin is stale or empty.
      if (!activeGstin || !list.some(s => s.gstin === activeGstin && s.authenticated)) {
        setActiveGstin(preferred);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { loadSessions(); }, []);

  const activeSess = sessions.find(s => s.gstin === activeGstin);

  const fetchReport = async () => {
    if (!activeGstin) { toast.error("Authenticate a GSTIN first"); return; }
    if (!activeSess?.authenticated) {
      if (activeSess?.via === "zenith360_gst") {
        toast.warning("Zenith360-GST shows no live gst.gov.in login for this GSTIN — trying anyway");
      } else {
        toast.error("Session not active");
        return;
      }
    }
    setLoading(true); setResult(null);
    const r = REPORTS.find(x => x.key === tab);
    try {
      const params = { gstin: activeGstin };
      if (r.kind === "period") {
        if (mode === "period") params.period = period; else params.fy = fy;
        if (r.sections) params.section = section;
      } else if (r.kind === "range") {
        params.from_dt = fromDt; params.to_dt = toDt;
      } else if (r.kind === "fy_only") {
        params.fy = fy;
      }
      const resp = await api.get(`/gst-reports/${tab}`, { params });
      setResult(resp.data);
      toast.success("Report fetched");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    }
    setLoading(false);
  };

  const refreshNow = async (gstin) => {
    try {
      await api.post("/gst-reports/auth/refresh-now", { gstin });
      toast.success("Session refreshed");
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
  };

  const fileGstr1 = async () => {
    if (!activeGstin || !activeSess?.authenticated) {
      toast.error("Authenticate GSTIN first"); return;
    }
    // Load timeline first
    try {
      const t = await api.get("/gst-reports/gstr1/filing-timeline",
        { params: { gstin: activeGstin, fy } });
      setTimeline(t.data.timeline || []);
    } catch { /* ignore */ }
    if (!confirm(`Push GSTR-1 for ${activeGstin} · period ${period} to GSTN?\n`
                   + "This will SAVE the return on the government portal (does NOT file). "
                   + "You'll be prompted to complete EVC/DSC afterwards on gst.gov.in.")) return;
    setFileBusy(true);
    try {
      const prev = await api.get(`/gstr1/payload/${period}`);
      const r = await api.post("/gst-reports/gstr1/file",
        { gstin: activeGstin, period, action: "SAVE", payload: prev.data });
      toast.success(`GSTR-1 draft saved on GSTN portal for period ${period}`);
      setResult(r.data);
      // refresh timeline
      const t = await api.get("/gst-reports/gstr1/filing-timeline",
        { params: { gstin: activeGstin, fy } });
      setTimeline(t.data.timeline || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "GSTR-1 filing failed");
    }
    setFileBusy(false);
  };

  const reconcile2b = async () => {
    if (!activeGstin || !activeSess?.authenticated) {
      toast.error("Authenticate GSTIN first"); return;
    }
    setRecoBusy(true); setReco(null);
    try {
      const r = await api.get("/gst-reports/gstr2b/reconcile",
        { params: { gstin: activeGstin, period } });
      setReco(r.data);
      toast.success("Reconciliation done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconciliation failed");
    }
    setRecoBusy(false);
  };

  const downloadExcel = async () => {
    if (!activeGstin || !activeSess?.authenticated) {
      toast.error("Authenticate a GSTIN first"); return;
    }
    const r = REPORTS.find(x => x.key === tab);
    if (!r?.excel) { toast.error("Excel export not available for this report"); return; }
    setXlsxBusy(true);
    try {
      const params = { gstin: activeGstin };
      if (r.kind === "period") {
        params.fy = fy;
        if (mode === "period") params.months = period;
      } else if (r.kind === "range") {
        // Auto-fill FY range when the user hasn't set explicit dates
        const startYr = parseInt(fy.split("-")[0], 10);
        params.from_dt = fromDt || `01-04-${startYr}`;
        params.to_dt   = toDt   || `31-03-${startYr + 1}`;
      } else if (r.kind === "fy_only") {
        params.fy = fy;
      }
      const resp = await api.get(`/gst-reports/exports/${tab}`,
        { params, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a   = document.createElement("a");
      a.href = url;
      const stamp = r.kind === "range"
        ? `${params.from_dt}_to_${params.to_dt}`
        : (mode === "period" ? period : fy);
      a.download = `${tab.toUpperCase()}_${activeGstin}_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (e?.response?.data instanceof Blob
            ? await e.response.data.text().catch(() => "Download failed")
            : "Download failed");
      toast.error(String(detail).slice(0, 200));
    }
    setXlsxBusy(false);
  };

  const logout = async (gstin) => {
    if (!confirm(`Logout session for ${gstin}?`)) return;
    try {
      await api.post("/gst-reports/auth/logout", { gstin });
      toast.success("Logged out");
      loadSessions();
      if (activeGstin === gstin) setActiveGstin("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Logout failed"); }
  };

  const activeReport = REPORTS.find(x => x.key === tab);

  /** Fetch all 3 ledger endpoints and blend into one time-series for the chart. */
  const loadLedgerTrend = async () => {
    if (!activeGstin || !activeSess?.authenticated) return;
    setLedgerBusy(true); setLedgerSeries([]);
    try {
      const startYr = parseInt(fy.split("-")[0], 10);
      const params  = {
        gstin: activeGstin,
        from_dt: fromDt || `01-04-${startYr}`,
        to_dt:   toDt   || `31-03-${startYr + 1}`,
      };
      const [cashR, credR, revR] = await Promise.allSettled([
        api.get("/gst-reports/cash-ledger",     { params }),
        api.get("/gst-reports/credit-ledger",   { params }),
        api.get("/gst-reports/reversal-ledger", { params }),
      ]);
      const cash     = cashR.status === "fulfilled" ? normaliseLedgerToSeries(cashR.value.data, "cash")         : [];
      const credit   = credR.status === "fulfilled" ? normaliseLedgerToSeries(credR.value.data, "credit")       : [];
      const reversal = revR.status  === "fulfilled" ? normaliseLedgerToSeries(revR.value.data,  "reversal")     : [];
      const merged = mergeLedgerSeries({ cash, credit, reversal });
      setLedgerSeries(merged);
      if (!merged.length) toast.info("No ledger entries in the selected range");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ledger trend load failed");
    }
    setLedgerBusy(false);
  };

  return (
    <div className="p-6 space-y-6" data-testid="gst-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-3xl font-bold">
            <ShieldCheck className="w-8 h-8 text-emerald-500" />
            <span>GST Reports</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Live from gst.gov.in through Zenith360-GST.
          </p>
        </div>
        {bridgeMode ? (
          <div className="text-xs text-muted-foreground max-w-xs" data-testid="gst-reports-bridge-note">
            Reports come live from gst.gov.in through Zenith360-GST. Log in to the GST portal once in Zenith360-GST.
          </div>
        ) : (
        <Button onClick={() => setAuthOpen(true)} data-testid="btn-add-gstin">
          <KeyRound className="w-4 h-4 mr-2" />
          Authenticate GSTIN
        </Button>
        )}
      </div>

      {/* Session cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sessions.length === 0 && (
          <Card className="col-span-3 border-dashed">
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No company GSTINs yet. Add them under Company GSTINs, then link them in Settings → Integrations.
            </CardContent>
          </Card>
        )}
        {sessions.map((s) => {
          const isActive = s.gstin === activeGstin;
          return (
            <Card key={s.gstin}
                    className={`cursor-pointer transition ${isActive ? "border-emerald-500 border-2" : ""}`}
                    onClick={() => setActiveGstin(s.gstin)}
                    data-testid={`sess-card-${s.gstin}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-mono text-base">{s.gstin}</CardTitle>
                  {s.authenticated
                    ? <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1"/>gst.gov.in login</Badge>
                    : <Badge variant="secondary">No portal login</Badge>}
                </div>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground space-y-1">
                <div>Portal user: <span className="font-mono">{s.gst_username || "—"}</span></div>
                <div>Login valid till: {s.token_expires_at ? new Date(s.token_expires_at).toLocaleString() : "—"}</div>
                <div>Managed in Zenith360-GST</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Reports panel */}
      {sessions.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <span>Reports ·</span>
                <span className="font-mono text-base" data-testid="active-gstin-label">
                  {activeGstin || "select a GSTIN above"}
                </span>
                {activeSess?.authenticated
                  ? <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1"/>gst.gov.in login live</Badge>
                  : activeGstin
                    ? <Badge variant="secondary">No live gst.gov.in login in Zenith360-GST</Badge>
                    : null}
              </CardTitle>
              {sessions.some(s => s.authenticated) && !activeSess?.authenticated && (
                <Button size="sm" variant="secondary" data-testid="btn-switch-active"
                          onClick={() => setActiveGstin(sessions.find(s => s.authenticated).gstin)}>
                  Switch to active GSTIN
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="flex flex-wrap h-auto">
                {REPORTS.map(r => (
                  <TabsTrigger key={r.key} value={r.key} data-testid={`tab-${r.key}`}>{r.label}</TabsTrigger>
                ))}
              </TabsList>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                {activeReport?.kind === "period" && (<>
                  <div>
                    <Label>Mode</Label>
                    <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                              value={mode} onChange={(e) => setMode(e.target.value)}
                              data-testid="input-mode">
                      <option value="period">Specific Month</option>
                      <option value="fy">Full Financial Year</option>
                    </select>
                  </div>
                  {mode === "period" ? (<>
                    <div>
                      <Label>Month</Label>
                      <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                                value={period.slice(0, 2)}
                                onChange={(e) => setPeriod(e.target.value + period.slice(2, 6))}
                                data-testid="input-month">
                        {MONTHS.map(m => <option key={m.v} value={m.v}>{m.n}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Year</Label>
                      <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                                value={period.slice(2, 6)}
                                onChange={(e) => setPeriod(period.slice(0, 2) + e.target.value)}
                                data-testid="input-year">
                        {Array.from({length:7},(_,i)=>String(new Date().getFullYear()-i)).map(y =>
                          <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </>) : (
                    <div>
                      <Label>Financial Year</Label>
                      <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                                value={fy} onChange={(e) => setFy(e.target.value)}
                                data-testid="input-fy">
                        {FY_LIST.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  )}
                  {activeReport.sections && (
                    <div>
                      <Label>Section</Label>
                      <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                                value={section} onChange={(e) => setSection(e.target.value)}
                                data-testid="input-section">
                        {activeReport.sections.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                      </select>
                    </div>
                  )}
                </>)}
                {activeReport?.kind === "range" && (<>
                  <div>
                    <Label>Financial Year (default)</Label>
                    <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                              value={fy} onChange={(e) => {
                                setFy(e.target.value);
                                setFromDt(""); setToDt("");
                              }}
                              data-testid="input-fy-range">
                      {FY_LIST.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>From (DD-MM-YYYY)</Label>
                    <Input value={fromDt}
                             onChange={(e) => setFromDt(e.target.value)}
                             placeholder={`01-04-${fy.split("-")[0]}`}
                             data-testid="input-from" />
                  </div>
                  <div>
                    <Label>To (DD-MM-YYYY)</Label>
                    <Input value={toDt}
                             onChange={(e) => setToDt(e.target.value)}
                             placeholder={`31-03-${parseInt(fy.split("-")[0])+1}`}
                             data-testid="input-to" />
                  </div>
                  <div className="flex items-end">
                    <Button variant="outline" onClick={loadLedgerTrend}
                              disabled={ledgerBusy || !activeGstin || !activeSess?.authenticated}
                              data-testid="btn-ledger-trend">
                      {ledgerBusy ? "Charting…" : "View Trend Chart"}
                    </Button>
                  </div>
                </>)}
                {activeReport?.kind === "fy_only" && (
                  <div>
                    <Label>Financial Year</Label>
                    <select className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                              value={fy} onChange={(e) => setFy(e.target.value)}
                              data-testid="input-fy-only">
                      {FY_LIST.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={fetchReport} disabled={loading || !activeGstin}
                            data-testid="btn-fetch-report">
                    {loading ? "Fetching…" : "Fetch Report"}
                  </Button>
                  {activeReport?.excel && (
                    <Button variant="secondary" onClick={downloadExcel}
                              disabled={xlsxBusy || !activeGstin || !activeSess?.authenticated}
                              data-testid="btn-download-xlsx">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      {xlsxBusy ? "Building…" : "Download Excel"}
                    </Button>
                  )}
                  {tab === "gstr1" && (
                    <span className="text-xs text-muted-foreground self-center" data-testid="gstr1-file-note">File GSTR-1 in Zenith360-GST</span>
                  )}
                  {tab === "gstr2b" && activeSess?.authenticated && mode === "period" && (
                    <>
                      <Button variant="outline" onClick={reconcile2b}
                                disabled={recoBusy}
                                data-testid="btn-reconcile-2b">
                        {recoBusy ? "Matching…" : "Reconcile with Purchases"}
                      </Button>
                      <Button variant="outline"
                                onClick={async () => {
                                  const pan = activeGstin.slice(2, 12);
                                  setRecoBusy(true);
                                  try {
                                    const r = await api.get("/gst-reports/gstr2b/reconcile-pan",
                                      { params: { pan, period } });
                                    setReco({
                                      summary: {
                                        matched_count: 0, mismatched_count: 0,
                                        missing_in_2b: 0, missing_in_books: 0,
                                        total_2b_itc: r.data.aggregate.total_2b_itc,
                                        reconciliation_pct: 0,
                                      },
                                      matched: [], mismatched: [],
                                      missing_in_2b: [],
                                      missing_in_books: r.data.gstins.map(g => ({
                                        supplier_gstin: g.gstin,
                                        invoice_no: `${g.invoices} invoices`,
                                        invoice_value: g.itc, igst: 0, cgst: 0, sgst: 0,
                                      })),
                                    });
                                    toast.success(`PAN-wide: ${r.data.gstins.length} GSTINs, ₹${Number(r.data.aggregate.total_2b_itc).toLocaleString("en-IN")} ITC`);
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || "PAN reco failed");
                                  }
                                  setRecoBusy(false);
                                }}
                                data-testid="btn-pan-reco">
                        PAN-wide 2B
                      </Button>
                    </>
                  )}
                </div>
                {tab === "gstr1" && timeline && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="gstr1-timeline">
                    {timeline.map(t => (
                      <div key={t.period}
                             title={`${t.period}${t.arn ? ` · ARN ${t.arn}` : ""}${t.dof ? ` · Filed ${t.dof}` : ""}`}
                             className={`px-2 py-1 rounded text-[10px] font-mono ${
                               t.state === "FILED" ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" :
                               t.state === "ERROR" ? "bg-red-500/20 text-red-700" :
                               "bg-muted text-muted-foreground"
                             }`}>
                        {t.period.slice(0,2)}/{t.period.slice(4)}
                        {t.state === "FILED" && " ✓"}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6">
                {activeReport?.kind === "range" && ledgerSeries.length > 0 && (
                  <div className="mb-4">
                    <LedgerTrendChart series={ledgerSeries} />
                  </div>
                )}
                {reco && tab === "gstr2b" ? (
                  <div className="space-y-4" data-testid="reco-result">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[
                        { k: "matched",         label: "Matched",           value: reco.summary.matched_count,    color: "emerald", rows: reco.matched },
                        { k: "mismatched",      label: "Mismatched",        value: reco.summary.mismatched_count, color: "amber",   rows: reco.mismatched },
                        { k: "missing-in-books",label: "In GSTN not Books", value: reco.summary.missing_in_books, color: "red",     rows: reco.missing_in_books },
                        { k: "missing-in-2b",   label: "In Books not GSTN", value: reco.summary.missing_in_2b,    color: "red",     rows: reco.missing_in_2b },
                        { k: "pct",             label: "Reconciled %",      value: `${reco.summary.reconciliation_pct}%`, color: "primary", rows: null },
                      ].map(t => (
                        <button key={t.k}
                                  onClick={() => t.rows !== null && setRecoDrill(t)}
                                  disabled={t.rows === null || t.rows.length === 0}
                                  className={`p-3 border rounded text-left transition
                                    bg-${t.color}-500/10 hover:bg-${t.color}-500/20
                                    disabled:opacity-60 disabled:cursor-default`}
                                  data-testid={`reco-tile-${t.k}`}>
                          <div className="text-xs text-muted-foreground">{t.label}</div>
                          <div className={`text-2xl font-bold text-${t.color}-600`}>{t.value}</div>
                        </button>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ₹{Number(reco.summary.total_2b_itc).toLocaleString("en-IN")} ITC available in GSTR-2B for {period} · Click any tile to drill down
                    </div>
                    {recoDrill && recoDrill.rows && (
                      <div className="border rounded overflow-hidden" data-testid="reco-drill">
                        <div className="flex items-center justify-between px-3 py-2 bg-muted">
                          <div className="text-sm font-semibold">{recoDrill.label} · {recoDrill.rows.length} rows</div>
                          <div className="flex gap-2">
                            {recoDrill.k === "missing-in-books" && recoDrill.rows.length > 0 && (
                              <Button size="sm" onClick={async () => {
                                if (!confirm(`Auto-book ${recoDrill.rows.length} missing invoices as draft Purchase Bills?`)) return;
                                try {
                                  const r = await api.post("/gst-reports/gstr2b/autobook",
                                    { gstin: activeGstin, period,
                                      invoices: recoDrill.rows.map(x => x.gstn || x) });
                                  toast.success(`${r.data.created} draft bills created${r.data.skipped ? ` · ${r.data.skipped} already existed` : ""}`);
                                  reconcile2b();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Auto-book failed");
                                }
                              }} data-testid="btn-autobook">Auto-Book All ({recoDrill.rows.length})</Button>
                            )}
                            <Button size="sm" variant="outline" onClick={async () => {
                              try {
                                const resp = await api.get("/gst-reports/exports/gstr2b-reco",
                                  { params: { gstin: activeGstin, period }, responseType: "blob" });
                                const url = URL.createObjectURL(resp.data);
                                const a = document.createElement("a"); a.href = url;
                                a.download = `Reconciliation_${activeGstin}_${period}.xlsx`;
                                document.body.appendChild(a); a.click(); a.remove();
                                URL.revokeObjectURL(url);
                              } catch (e) { toast.error("Reco XLSX failed"); }
                            }} data-testid="btn-reco-xlsx">
                              <FileSpreadsheet className="w-4 h-4 mr-1" /> Full XLSX
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRecoDrill(null)}
                                      data-testid="btn-close-drill">Close</Button>
                          </div>
                        </div>
                        <div className="max-h-96 overflow-auto">
                          <table className="min-w-full text-xs">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="p-2 text-left">Supplier GSTIN</th>
                                <th className="p-2 text-left">Invoice #</th>
                                <th className="p-2 text-right">GSTN Value</th>
                                <th className="p-2 text-right">Book Value</th>
                                <th className="p-2 text-right">GSTN Tax (IGST/CGST/SGST)</th>
                                <th className="p-2">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recoDrill.rows.map((r, i) => {
                                const g = r.gstn || r;
                                const b = r.book;
                                const g_gstin = g.supplier_gstin || b?.vendor_gstin || "—";
                                const inv_no  = g.invoice_no || b?.bill_no || "—";
                                return (
                                  <tr key={`${g_gstin}-${inv_no}-${i}`} className="border-t hover:bg-muted/20">
                                    <td className="p-2 font-mono">{g_gstin}</td>
                                    <td className="p-2 font-mono">{inv_no}</td>
                                    <td className="p-2 text-right">₹{Number(g.invoice_value || 0).toLocaleString("en-IN")}</td>
                                    <td className="p-2 text-right">{b ? `₹${Number(b.total || 0).toLocaleString("en-IN")}` : "—"}</td>
                                    <td className="p-2 text-right">₹{Number((g.igst || 0) + (g.cgst || 0) + (g.sgst || 0)).toLocaleString("en-IN")}</td>
                                    <td className="p-2">
                                      {b ? (
                                        <a href={`/app/purchases#bill-${b.bill_no}`} className="text-primary underline"
                                             data-testid={`btn-fix-${i}`}>Fix this</a>
                                      ) : (
                                        <span className="text-muted-foreground">Book entry missing</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : result ? (
                  activeReport?.kind === "fy_only" && tab === "filing-status" ? (
                    <FilingDashboard data={result} fy={fy} />
                  ) : (
                    <div className="p-6 rounded border bg-emerald-500/5 border-emerald-500/30
                                       flex items-center justify-between gap-4"
                           data-testid="fetch-status-card">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
                        <div className="text-sm">
                          <div className="font-medium">Report fetched from GSTN</div>
                          <div className="text-muted-foreground text-xs mt-0.5">
                            Data is available. Use <b>Download Excel</b> to get the full
                            summary, combined and modified sheets.
                          </div>
                        </div>
                      </div>
                      {activeReport?.excel && (
                        <Button onClick={downloadExcel} disabled={xlsxBusy}
                                  data-testid="btn-download-xlsx-inline">
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          {xlsxBusy ? "Building…" : "Download Excel"}
                        </Button>
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-sm text-muted-foreground p-8 text-center border rounded">
                    Choose a report + period and click <b>Fetch</b>.
                  </div>
                )}
              </div>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <GstAuthenticateModal open={authOpen} onOpenChange={setAuthOpen}
                                onSuccess={() => { setAuthOpen(false); loadSessions(); }} />
    </div>
  );
}


// ---------------------- Filing Dashboard (on-screen) ----------------------
function FilingDashboard({ data, fy }) {
  const root = data?.data?.data || data?.data || {};
  let entries = [];
  for (const k of ["EFiledlist", "ret_status_list", "rtnStatusList", "retlist", "list"]) {
    if (Array.isArray(root[k]) && root[k].length) { entries = root[k]; break; }
  }
  if (!entries.length && Array.isArray(root)) entries = root;

  if (!entries.length) {
    return (
      <div className="p-8 text-center border rounded text-sm text-muted-foreground"
             data-testid="filing-empty">
        No filing records returned by GSTN for FY {fy}. Try a different FY or
        confirm returns have been filed for this GSTIN.
      </div>
    );
  }

  const startYr = parseInt(fy.split("-")[0], 10);
  const monthsSeq = [
    { p: `04${startYr}`,     n: "Apr" }, { p: `05${startYr}`,     n: "May" },
    { p: `06${startYr}`,     n: "Jun" }, { p: `07${startYr}`,     n: "Jul" },
    { p: `08${startYr}`,     n: "Aug" }, { p: `09${startYr}`,     n: "Sep" },
    { p: `10${startYr}`,     n: "Oct" }, { p: `11${startYr}`,     n: "Nov" },
    { p: `12${startYr}`,     n: "Dec" }, { p: `01${startYr + 1}`, n: "Jan" },
    { p: `02${startYr + 1}`, n: "Feb" }, { p: `03${startYr + 1}`, n: "Mar" },
  ];
  const returnTypes = Array.from(new Set(entries.map(e =>
    String(e.rtntype || e.return_type || "").toUpperCase()
  ))).filter(Boolean).sort();

  const cellFor = (rt, p) => {
    const e = entries.find(x =>
      String(x.rtntype || x.return_type || "").toUpperCase() === rt &&
      String(x.ret_prd || x.period || "") === p
    );
    if (!e) return null;
    return {
      dof:    e.dof || e.filed_dt || e.filedDt || null,
      status: e.status || (e.dof ? "Filed" : "Not Filed"),
      mof:    (e.mof || e.mode_of_filing || "").toUpperCase(),
      arn:    e.arn || "",
    };
  };

  return (
    <div className="space-y-3" data-testid="filing-dashboard">
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-xs">
          <thead className="bg-primary/10">
            <tr>
              <th className="p-2 text-left font-semibold sticky left-0 bg-primary/10 z-10">Return Type</th>
              {monthsSeq.map(m => (
                <th key={m.p} className="p-2 text-center font-semibold whitespace-nowrap">{m.n}</th>
              ))}
              <th className="p-2 text-center font-semibold whitespace-nowrap">Filed / Total</th>
            </tr>
          </thead>
          <tbody>
            {returnTypes.map(rt => {
              let filed = 0;
              const cells = monthsSeq.map(m => {
                const c = cellFor(rt, m.p);
                if (c && (c.status?.toLowerCase() === "filed" || c.dof)) filed++;
                return { m, c };
              });
              return (
                <tr key={rt} className="border-t hover:bg-muted/30">
                  <td className="p-2 font-mono font-semibold sticky left-0 bg-background">{rt}</td>
                  {cells.map(({ m, c }) => (
                    <td key={m.p}
                          className={`p-2 text-center align-top ${
                            !c ? "text-muted-foreground bg-muted/20" :
                            (c.status?.toLowerCase() === "filed" || c.dof)
                              ? "bg-emerald-500/10" : "bg-red-500/10"
                          }`}>
                      {c ? (
                        <div className="space-y-0.5">
                          <div className="font-medium">{c.status || "—"}</div>
                          {c.dof && <div className="text-[10px] text-muted-foreground">{c.dof}</div>}
                          {c.mof && <div className="text-[10px]">{c.mof}</div>}
                        </div>
                      ) : "—"}
                    </td>
                  ))}
                  <td className="p-2 text-center font-semibold">{filed} / {monthsSeq.length}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground">
        Green = filed on time · Red = not filed · Grey = no data returned by GSTN.
      </div>
    </div>
  );
}
